import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadDiagnosticPanel = ({ 
  activeBucket, 
  uploadLogs = [], 
  s3ClientStatus = null,
  isExpanded = false,
  onToggle = null 
}) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all'); // all, success, error, warning, info

  useEffect(() => {
    // Update logs from props
    if (uploadLogs?.length > 0) {
      setLogs(uploadLogs);
    }
  }, [uploadLogs]);

  const getLogIcon = (type) => {
    switch (type) {
      case 'success': return 'CheckCircle2';
      case 'error': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      case 'info': return 'Info';
      default: return 'FileText';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'info': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800';
      case 'error': return 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800';
      case 'info': return 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800';
      default: return 'bg-gray-50 dark:bg-gray-900/10 border-gray-200 dark:border-gray-800';
    }
  };

  const filteredLogs = logs?.filter(log => 
    filter === 'all' || log?.type === filter
  );

  const errorCount = logs?.filter(l => l?.type === 'error')?.length;
  const warningCount = logs?.filter(l => l?.type === 'warning')?.length;
  const successCount = logs?.filter(l => l?.type === 'success')?.length;

  const copyLogsToClipboard = () => {
    const logText = logs?.map(log => 
      `[${log?.timestamp}] ${log?.type?.toUpperCase()}: ${log?.message}`
    )?.join('\n');
    
    navigator?.clipboard?.writeText(logText);
  };

  const exportLogs = () => {
    const logText = logs?.map(log => 
      `[${log?.timestamp}] ${log?.type?.toUpperCase()}: ${log?.message}\n${log?.details ? JSON.stringify(log?.details, null, 2) : ''}`
    )?.join('\n\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upload-diagnostics-${new Date()?.toISOString()?.split('T')?.[0]}.txt`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Upload Diagnostics</h3>
              <p className="text-xs text-purple-100">Real-time debugging and troubleshooting</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {logs?.length > 0 && (
              <div className="flex items-center gap-2 mr-3">
                {successCount > 0 && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-100 text-xs rounded-md">
                    ✓ {successCount}
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-100 text-xs rounded-md">
                    ⚠ {warningCount}
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-100 text-xs rounded-md">
                    ✕ {errorCount}
                  </span>
                )}
              </div>
            )}
            
            <button
              onClick={onToggle}
              className="p-2 hover:bg-white/10 rounded-md transition-colors"
            >
              <Icon 
                name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                size={20} 
                className="text-white" 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Configuration Status */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Settings" size={16} className="text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">Configuration Status</h4>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">S3 Client:</span>
                <span className={`flex items-center gap-1 ${s3ClientStatus ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  <Icon name={s3ClientStatus ? "CheckCircle2" : "XCircle"} size={14} />
                  {s3ClientStatus ? 'Initialized' : 'Not Initialized'}
                </span>
              </div>
              
              {activeBucket && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Bucket Name:</span>
                    <span className="font-mono text-xs text-foreground">{activeBucket?.bucketName}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Region:</span>
                    <span className="font-mono text-xs text-foreground">{activeBucket?.bucketRegion}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Access Key:</span>
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Icon name="CheckCircle2" size={14} />
                      Configured
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Secret Key:</span>
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Icon name="CheckCircle2" size={14} />
                      Configured
                    </span>
                  </div>

                  {activeBucket?.publicUrl && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Public URL:</span>
                      <span className="font-mono text-xs text-foreground truncate max-w-[200px]">
                        {activeBucket?.publicUrl}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Log Filters */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  filter === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All ({logs?.length})
              </button>
              <button
                onClick={() => setFilter('error')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  filter === 'error' ?'bg-red-500 text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Errors ({errorCount})
              </button>
              <button
                onClick={() => setFilter('warning')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  filter === 'warning' ?'bg-yellow-500 text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Warnings ({warningCount})
              </button>
              <button
                onClick={() => setFilter('success')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  filter === 'success' ?'bg-green-500 text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Success ({successCount})
              </button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={copyLogsToClipboard}
                iconName="Copy"
                disabled={logs?.length === 0}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={exportLogs}
                iconName="Download"
                disabled={logs?.length === 0}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={clearLogs}
                iconName="Trash2"
                disabled={logs?.length === 0}
              />
            </div>
          </div>

          {/* Logs Display */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin">
            {filteredLogs?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="FileText" size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No logs available</p>
                <p className="text-xs">Upload diagnostics will appear here</p>
              </div>
            ) : (
              filteredLogs?.map((log, index) => (
                <div 
                  key={index} 
                  className={`border rounded-lg p-3 ${getBgColor(log?.type)}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon 
                      name={getLogIcon(log?.type)} 
                      size={18} 
                      className={`mt-0.5 ${getLogColor(log?.type)}`} 
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium ${getLogColor(log?.type)}`}>
                          {log?.type?.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {log?.timestamp}
                        </span>
                      </div>
                      <p className="text-sm text-foreground break-words">{log?.message}</p>
                      
                      {log?.details && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            Show Details
                          </summary>
                          <pre className="mt-2 p-2 bg-black/5 dark:bg-white/5 rounded text-xs overflow-x-auto">
                            {JSON.stringify(log?.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Troubleshooting Tips */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Lightbulb" size={18} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1 text-sm">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Troubleshooting Tips
                </h4>
                <ul className="space-y-1 text-blue-700 dark:text-blue-300 text-xs">
                  <li>• If uploads fail with CORS errors, check your S3 bucket CORS configuration</li>
                  <li>• Ensure your AWS credentials have PutObject permissions</li>
                  <li>• Verify the bucket name and region are correct</li>
                  <li>• Check browser console (F12) for detailed error messages</li>
                  <li>• For network errors, verify your internet connection and firewall settings</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDiagnosticPanel;